import { useEffect, useMemo, useRef, useState } from 'react';
import { clubIndex, type ClubIndexRound } from '@/lib/board/club-index';
import { Award, Camera, ChevronRight, Heart, LogOut, Send } from 'lucide-react';
import { db } from '@/lib/db';
import type { Ace, Invitation, Member, MemberWithStats, PostEnriched } from '@/lib/db/types';
import { getSeasonTier, getSeasonYear, seasonDateRange, seasonLabel } from '@/lib/reference/season-tiers';
import { loadHandicapHistory } from '@/lib/handicap-history';
import { Avatar } from '@/components/shared/Avatar';
import { Crest } from '@/components/shared/Crest';
import { AvatarPicker } from '@/components/profile/AvatarPicker';
import { EditAboutModal } from '@/components/profile/EditAboutModal';
import { EditDisplayNameModal } from '@/components/profile/EditDisplayNameModal';
import { EditServiceModal } from '@/components/profile/EditServiceModal';
import { PrivacySheet } from '@/components/profile/PrivacySheet';
import { SuggestionBoxSheet } from '@/components/profile/SuggestionBoxSheet';
import { AdminModerationSheet } from '@/components/profile/AdminModerationSheet';
import { HoleInOneWall } from '@/components/profile/HoleInOneWall';
import { GhinConnectSheet } from '@/components/profile/GhinConnectSheet';
import { VenmoSheet } from '@/components/profile/VenmoSheet';
import { ProfilePhotoModal } from '@/components/profile/ProfilePhotoModal';
import { GivingLogSheet } from '@/components/profile/GivingLogSheet';
import { FantasyCardSheet } from '@/components/profile/FantasyCardSheet';
import { RoundSnapshotSheet } from '@/components/profile/RoundSnapshotSheet';
import { ProfileMemberCard } from '@/components/profile/ProfileMemberCard';
import { personalizeRoundPostForMember } from '@/lib/profile/member-round-history';
import { holesLabel, playedPar, scoreToParLabel } from '@/lib/scoring/played-par';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  member: Member;
  onMemberUpdate?: (m: Member) => void;
  onOpenProfile?: (id: string) => void;
  onOpenClubhouse?: () => void;
}

import { formatServiceTag } from '@/lib/service-format';

type SheetKey = 'avatar' | 'photo' | 'name' | 'about' | 'service' | 'privacy' | 'suggestion' | 'admin' | 'aces' | 'ghin' | 'ancc' | 'venmo' | 'giving' | 'fantasy' | null;

function scoreLabel(score: number, par: number): string {
  return scoreToParLabel(score, par);
}

export function ProfileTab({ member: initialMember, onMemberUpdate, onOpenProfile, onOpenClubhouse }: Props) {
  const [member, setMember] = useState(initialMember);
  const [profile, setProfile] = useState<MemberWithStats | null>(null);
  const [posts, setPosts] = useState<PostEnriched[]>([]);
  const [aces, setAces] = useState<Ace[]>([]);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [givingCents, setGivingCents] = useState(0);
  const [seasonPoints, setSeasonPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<SheetKey>(null);
  const [postsExpanded, setPostsExpanded] = useState(false);
  const [selectedRoundPost, setSelectedRoundPost] = useState<PostEnriched | null>(null);
  const [clubAbbr, setClubAbbr] = useState('ANCC');
  const hasLoaded = useRef(false);

  useEffect(() => { setMember(initialMember); }, [initialMember]);

  const load = async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent && !hasLoaded.current) setLoading(true);
    setError(null);
    try {
      const year = getSeasonYear();
      const { start, end } = seasonDateRange(year);
      const [p, roundHistory, ac, inv, give, club, seasonPts] = await Promise.all([
        db.members.getWithStats(member.id),
        db.posts.listMemberRoundHistory(member.id, { limit: 80, includeCourseHoles: true }),
        db.aces.forMember(member.id),
        db.invitations.list(),
        db.donations.totalCents(member.id),
        db.clubs.get(member.club_id),
        db.points.totalInRange(member.id, start.toISOString(), end.toISOString()),
      ]);
      if (!p) throw new Error('Could not load your profile. Try signing out and back in.');
      setProfile(p);
      const personalizedRounds = roundHistory.map((post) => personalizeRoundPostForMember(post, member.id));
      const roundIds = new Set(personalizedRounds.map((post) => post.id));
      const [authoredOther] = await Promise.all([
        db.posts.list({ authorId: member.id, limit: 80, includeCourseHoles: true }),
      ]);
      const otherPosts = authoredOther.filter((post) => !roundIds.has(post.id));
      setPosts(
        [...personalizedRounds, ...otherPosts].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ),
      );
      setAces(ac);
      setInvites(inv.filter((i) => i.status === 'pending'));
      setGivingCents(give);
      setSeasonPoints(seasonPts);
      setClubAbbr(club?.abbr ?? 'ANCC');
      hasLoaded.current = true;
    } catch (err) {
      setError((err as Error).message || 'Unable to load profile');
      if (!hasLoaded.current) setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    hasLoaded.current = false;
    void load();
  }, [member.id]);

  const saveMember = (m: Member) => {
    setMember(m);
    onMemberUpdate?.(m);
    void load();
  };

  const clubIndexRounds = useMemo((): ClubIndexRound[] => (
    posts
      .filter((p) => p.type === 'round' && p.round?.gross_total != null && p.round.holes === 'full' && p.round.tee)
      .map((p) => ({
        member_id: member.id,
        gross_total: p.round!.gross_total!,
        holes: p.round!.holes,
        course_rating: p.round!.tee!.course_rating,
        slope: p.round!.tee!.slope,
        played_at: p.round!.played_at,
      }))
  ), [posts, member.id]);
  const anccIndexValue = clubIndex(clubIndexRounds, member.id);
  const anccIndex = anccIndexValue != null ? anccIndexValue.toFixed(1) : null;

  if (loading) return <div className="p-4 pt-4 text-sm text-mute">Loading profile…</div>;
  if (error || !profile) {
    return (
      <div className="space-y-3 p-4 pt-4">
        <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{error || 'Profile unavailable'}</div>
        <button type="button" onClick={() => void load()} className="rounded-full bg-navy px-4 py-2 text-sm font-semibold text-cream">Retry</button>
      </div>
    );
  }

  const seasonTier = getSeasonTier(seasonPoints);
  const seasonYear = getSeasonYear();
  const pendingInvites = invites.length;
  const roundPosts = posts.filter((p) => p.type === 'round' && p.round?.gross_total != null);
  const otherPosts = posts.filter((p) => p.type !== 'round' || !p.round?.gross_total);
  const visibleRoundPosts = roundPosts.slice(0, 3);
  const hiddenRoundPosts = roundPosts.slice(3);
  const visibleOtherPosts = otherPosts.slice(0, 3);
  const hiddenOtherPosts = otherPosts.slice(3);
  const hiddenPostCount = hiddenRoundPosts.length + hiddenOtherPosts.length;
  const serviceLabel = formatServiceTag(profile.service_branch, profile.service_rank, profile.service_status);
  const givingTotal = givingCents > 0 ? `$${(givingCents / 100).toFixed(0)}` : '$0';
  const isFollowersOnly = profile.visibility === 'followers';
  const memberSinceYear = String(new Date(member.created_at).getFullYear());

  const hcpHistory = loadHandicapHistory(member.id);

  return (
    <div className="space-y-4 pt-4 pb-8">
      <div className="relative mb-3.5 overflow-hidden rounded-[18px] bg-navy p-[22px_20px] text-paper shadow-bt-md">
        <div className="pointer-events-none absolute -right-6 -top-6 opacity-[0.08]">
          <Crest size={160} variant="full" />
        </div>
        <div className="mb-[18px] flex items-center gap-[14px]">
          <button type="button" onClick={() => setSheet('avatar')} className="relative shrink-0">
            <Avatar member={member} size={68} />
            <span className="absolute -bottom-0.5 -right-0.5 flex h-7 w-7 items-center justify-center rounded-full border-2 border-navy bg-gold text-navy"><Camera size={14} /></span>
          </button>
          <div className="min-w-0 flex-1">
            <button type="button" onClick={() => setSheet('name')} className="text-left font-serif text-2xl font-bold tracking-tight hover:text-gold">{profile.name}</button>
            <div className="mt-0.5 text-sm font-medium text-white/60">@{profile.handle}</div>
            {serviceLabel && (
              <div className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-gold/15 px-2 py-0.5 text-[11px] font-bold tracking-wide text-gold">
                🎖 {serviceLabel}
              </div>
            )}
            {aces.length > 0 && (
              <div className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-gold/20 px-2 py-0.5 text-[11px] font-bold tracking-wide text-gold">
                🦅 {aces.length} ACE{aces.length === 1 ? '' : 'S'}
              </div>
            )}
            <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2.5 py-1 text-xs font-bold text-gold">
              {seasonTier.badge} {seasonTier.name} · {seasonPoints} pts · {seasonLabel(seasonYear)}
            </div>
            <div className="mt-1 text-[10px] font-medium text-white/50">
              {profile.total_points} career Club Points
            </div>
          </div>
          <button type="button" onClick={() => db.auth.signOut()} className="shrink-0 rounded-full bg-white/10 p-2"><LogOut size={18} /></button>
        </div>
      </div>

      <ProfileMemberCard
        handle={profile.handle}
        profilePhotoUrl={member.profile_photo_url}
        aboutMe={profile.about_me}
        handicap={profile.handicap}
        ghinConnected={member.ghin_connected}
        anccIndex={anccIndex}
        qualifyingRounds={clubIndexRounds.length}
        acesCount={aces.length}
        givingDollars={givingTotal}
        venmoConnected={!!profile.venmo}
        visibility={isFollowersOnly ? 'followers' : 'public'}
        serviceLabel={serviceLabel}
        clubAbbr={clubAbbr}
        memberSinceYear={memberSinceYear}
        onEditPhoto={() => setSheet('photo')}
        onEditAbout={() => setSheet('about')}
        onConnectGhin={() => setSheet('ghin')}
        onOpenAnccIndex={() => setSheet('ancc')}
        onOpenAces={() => setSheet('aces')}
        onOpenGiving={() => setSheet('giving')}
        onEditService={() => setSheet('service')}
        onOpenIdeas={() => setSheet('suggestion')}
        onOpenVenmo={() => setSheet('venmo')}
        onOpenPrivacy={() => setSheet('privacy')}
      />

      {member.is_admin && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setSheet('admin')}
            className="flex w-full items-center gap-3 rounded-xl border border-line bg-paper p-3.5 text-left shadow-bt active:scale-[0.99]"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-navy text-[10px] font-extrabold text-gold">
              ADM
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-navy">Review Feedback</div>
              <div className="mt-0.5 text-[11px] text-mute">Member suggestions &amp; bugs appear here.</div>
            </div>
            <ChevronRight size={18} className="shrink-0 text-mute" />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setSheet('fantasy')}
        className="mb-4 flex w-full items-center gap-3 rounded-xl border border-line bg-gradient-to-br from-navy/[0.06] to-gold/10 p-3.5 text-left"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-navy text-gold">
          <Award size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold tracking-[0.15em] text-mute">FANTASY & RINGER CARDS</div>
          <div className="mt-0.5 text-[13px] font-bold text-navy">54-hole fantasy + career best nines</div>
        </div>
        <ChevronRight size={18} className="shrink-0 text-mute" />
      </button>

      <div className="mb-4 rounded-xl border border-line bg-gradient-to-br from-navy/[0.05] to-gold/[0.08] p-3.5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-navy text-gold">
            <Heart size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold tracking-[0.15em] text-mute">COMMUNITY GIVING</div>
            <div className="mt-0.5 text-[13px] font-bold text-navy">Arlington National Golf Association</div>
          </div>
          <div className="text-right">
            <div className="font-serif text-[22px] font-bold leading-none text-navy">{givingTotal}</div>
            <div className="mt-0.5 text-[9px] tracking-wide text-mute">DONATED</div>
          </div>
        </div>
        <p className="mt-2.5 text-[11px] leading-relaxed text-ink-soft">
          {givingCents > 0
            ? "You've donated through Bag Tag. Self-reported via Venmo; Bag Tag does not handle the funds."
            : 'Won money in a game? After settling, opt to donate a percentage via Venmo.'}
        </p>
      </div>

      {hcpHistory.length > 0 && (
        <div className="mb-4 rounded-xl border border-line bg-paper p-3">
          <div className="mb-2 text-[10px] font-bold tracking-wide text-mute">HANDICAP HISTORY</div>
          <div className="flex flex-col gap-1.5">
            {[...hcpHistory].reverse().slice(0, 5).map((h, i) => {
              const trend = h.to < h.from ? 'down' : h.to > h.from ? 'up' : 'same';
              return (
                <div key={`${h.t}-${i}`} className="flex items-center gap-2 text-xs text-ink-soft">
                  <span className="flex-1">
                    <span className="text-mute">{h.from.toFixed(1)}</span>
                    <span className="mx-1.5 text-mute">→</span>
                    <span className="font-bold text-navy">{h.to.toFixed(1)}</span>
                    <span
                      className="ml-2 text-[10px] font-bold"
                      style={{ color: trend === 'down' ? '#15803d' : trend === 'up' ? '#dc2626' : '#94a3b8' }}
                    >
                      {trend === 'down' && `↓ ${(h.from - h.to).toFixed(1)}`}
                      {trend === 'up' && `↑ ${(h.to - h.from).toFixed(1)}`}
                    </span>
                  </span>
                  <span className="text-[10px] text-mute">
                    {new Date(h.t).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pendingInvites > 0 && (
        <div className="mb-4 rounded-xl border border-line bg-paper p-3">
          <div className="mb-1.5 flex items-center gap-1.5 px-1 font-serif text-[11px] font-bold tracking-[0.15em] text-gold-deep">
            <Send size={11} /> {pendingInvites} PENDING INVITATION{pendingInvites === 1 ? '' : 'S'}
          </div>
          <div className="space-y-2">
            {invites.slice(0, 3).map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-2 rounded-xl border border-dashed border-gold bg-cream-warm px-3 py-2.5 text-sm">
                <span className="min-w-0 truncate text-ink-soft">{inv.recipient_name || inv.email || inv.phone}</span>
                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => void db.invitations.resend(inv.id).then(() => load({ silent: true }))} className="text-xs font-bold text-navy">Resend</button>
                  <button type="button" onClick={() => void db.invitations.cancel(inv.id).then(() => load({ silent: true }))} className="text-xs font-bold text-red-600">Cancel</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-line bg-paper p-3">
        <div className="mb-2 flex items-center gap-2.5 pb-2.5 text-[13px] font-semibold text-ink-soft">
          <svg width="14" height="14" viewBox="0 0 14 14" className="shrink-0" aria-hidden>
            <rect x="0" y="9" width="1.5" height="5" fill="#8a6d3d" />
            <path d="M 1.5 2 L 7 2 Q 8 2 8 3 L 8 6 Q 8 7 7 7 L 1.5 7 Z" fill="#c4a662" stroke="#8a6d3d" strokeWidth="0.5" />
          </svg>
          My Rounds
        </div>
        {roundPosts.length === 0 && (
          <div className="rounded-xl border border-line bg-cream-warm p-6 text-center text-sm text-mute">Post your first round from the + button.</div>
        )}
        {visibleRoundPosts.map((p) => {
          const gross = p.round!.gross_total!;
          const holeRange = p.round!.holes ?? 'full';
          const par = p.round?.course
            ? playedPar(holeRange, p.round.course.par, p.round.course_holes)
            : (holeRange === 'full' ? 72 : 36);
          const course = p.round?.course?.name || p.metadata?.courseName || 'Course';
          const tee = p.round?.tee?.name || p.metadata?.teeName || '—';
          const holesText = p.round?.holes ? holesLabel(p.round.holes) : null;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedRoundPost(p)}
              className="mb-2 flex w-full items-center gap-3 rounded border border-line bg-cream-warm p-3 text-left"
            >
              <div className="flex h-[50px] w-[50px] shrink-0 flex-col items-center justify-center rounded bg-navy text-cream">
                <span className="font-serif text-lg font-bold leading-none">{gross}</span>
                <span className="font-mono text-[10px] text-gold">{scoreLabel(gross, par)}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif text-sm font-semibold text-ink">{course}</div>
                <div className="mt-0.5 text-[11px] text-mute">
                  {new Date(p.round?.played_at || p.created_at).toLocaleString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })} · {tee} tees{holesText ? ` · ${holesText}` : ''}
                </div>
              </div>
            </button>
          );
        })}
        {otherPosts.length > 0 && (
          <div className="mt-4 text-[13px] font-semibold text-ink-soft">Other posts</div>
        )}
        {visibleOtherPosts.map((p) => (
          <div key={p.id} className="mb-2 rounded-xl border border-line bg-cream-warm p-3 text-sm text-ink-soft">
            {p.body || `${p.type} post`}
          </div>
        ))}
        {postsExpanded && hiddenRoundPosts.map((p) => {
          const gross = p.round!.gross_total!;
          const holeRange = p.round!.holes ?? 'full';
          const par = p.round?.course
            ? playedPar(holeRange, p.round.course.par, p.round.course_holes)
            : (holeRange === 'full' ? 72 : 36);
          const course = p.round?.course?.name || p.metadata?.courseName || 'Course';
          const tee = p.round?.tee?.name || p.metadata?.teeName || '—';
          const holesText = p.round?.holes ? holesLabel(p.round.holes) : null;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedRoundPost(p)}
              className="mb-2 flex w-full items-center gap-3 rounded border border-line bg-cream-warm p-3 text-left"
            >
              <div className="flex h-[50px] w-[50px] shrink-0 flex-col items-center justify-center rounded bg-navy text-cream">
                <span className="font-serif text-lg font-bold leading-none">{gross}</span>
                <span className="font-mono text-[10px] text-gold">{scoreLabel(gross, par)}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif text-sm font-semibold text-ink">{course}</div>
                <div className="mt-0.5 text-[11px] text-mute">
                  {new Date(p.round?.played_at || p.created_at).toLocaleString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })} · {tee} tees{holesText ? ` · ${holesText}` : ''}
                </div>
              </div>
            </button>
          );
        })}
        {postsExpanded && hiddenOtherPosts.map((p) => (
          <div key={p.id} className="mb-2 rounded-xl border border-line bg-cream-warm p-3 text-sm text-ink-soft">
            {p.body || `${p.type} post`}
          </div>
        ))}
        {hiddenPostCount > 0 && (
          <button
            type="button"
            onClick={() => setPostsExpanded((v) => !v)}
            className="mt-2 w-full rounded-xl border border-line bg-cream-warm px-4 py-3 text-sm font-bold text-navy"
          >
            {postsExpanded ? 'Show less' : `Show ${hiddenPostCount} more post${hiddenPostCount === 1 ? '' : 's'}`}
          </button>
        )}
      </div>

      {sheet === 'avatar' && <AvatarPicker member={member} current={member.avatar} onClose={() => setSheet(null)} onSelect={(a) => void db.members.update({ avatar: a }).then(saveMember)} />}
      {sheet === 'name' && <EditDisplayNameModal member={member} onClose={() => setSheet(null)} onSaved={saveMember} />}
      {sheet === 'photo' && <ProfilePhotoModal member={member} onClose={() => setSheet(null)} onSaved={saveMember} />}
      {sheet === 'about' && <EditAboutModal member={member} onClose={() => setSheet(null)} onSaved={saveMember} />}
      {sheet === 'service' && <EditServiceModal member={member} onClose={() => setSheet(null)} onSaved={saveMember} />}
      {sheet === 'privacy' && <PrivacySheet member={member} onClose={() => setSheet(null)} onSaved={saveMember} />}
      {sheet === 'suggestion' && <SuggestionBoxSheet onClose={() => setSheet(null)} />}
      {sheet === 'admin' && member.is_admin && <AdminModerationSheet onClose={() => setSheet(null)} />}
      {sheet === 'aces' && <HoleInOneWall onClose={() => setSheet(null)} />}
      {sheet === 'ghin' && <GhinConnectSheet member={member} onClose={() => setSheet(null)} onSaved={saveMember} />}
      {sheet === 'ancc' && (
        <Sheet title={`${clubAbbr} Index`} onClose={() => setSheet(null)}>
          <div className="space-y-3 p-4 text-sm text-ink-soft">
            <p>
              Internal index from posted full rounds at ANCC courses. It is separate from your GHIN/self-reported handicap.
            </p>
            <div className="rounded-xl border border-line bg-paper p-3">
              <div className="text-[10px] font-bold uppercase tracking-wide text-mute">Current {clubAbbr} index</div>
              <div className="mt-1 font-serif text-3xl font-bold text-navy">{anccIndex ?? '—'}</div>
              <div className="mt-1 text-xs text-mute">
                Qualifying rounds: {clubIndexRounds.length}
                {clubIndexRounds.length < 3 ? ' (need 3+ to rank)' : ''}
              </div>
            </div>
          </div>
        </Sheet>
      )}
      {sheet === 'venmo' && <VenmoSheet member={member} onClose={() => setSheet(null)} onSaved={saveMember} />}
      {sheet === 'giving' && <GivingLogSheet onClose={() => setSheet(null)} onLogged={() => void load()} />}
      {sheet === 'fantasy' && <FantasyCardSheet posts={posts} memberId={member.id} onClose={() => setSheet(null)} />}
      {selectedRoundPost && (
        <RoundSnapshotSheet
          post={selectedRoundPost}
          viewerMemberId={member.id}
          onClose={() => setSelectedRoundPost(null)}
        />
      )}
    </div>
  );
}
